/**
 * MSR EVT-03 (mig 530): pure helpers for the MSR Calls to Action tab (kept out of the service module so page tests
 * that mock the service still get the real logic).
 */
import type { MsrEventCtaRow } from "../services/msrPortal.service";

/** The event's account CTA first (at most one), then the rep's own CTAs. */
export function splitMsrEventCtas(rows: MsrEventCtaRow[]): {
  account: MsrEventCtaRow | null;
  mine: MsrEventCtaRow[];
} {
  return {
    account: rows.find((r) => r.is_system_generic === true) ?? null,
    mine: rows.filter((r) => r.is_system_generic !== true),
  };
}

/** EVT-04 (owner 2026-10-12): shown to the MSR instead of an event QR that is not ready. */
export const EVENT_QR_NOT_READY =
  "The event QR isn't ready yet — ask the event team.";

/** Is the event QR withheld as not ready? Only an explicit false from the server (mig 530 v2) hides it. */
export function eventQrNotReady(row: MsrEventCtaRow | null): boolean {
  return row !== null && row.qr_ready === false;
}

/** One line under the account QR: where its tracking codes came from. */
export function describeAccountCta(row: MsrEventCtaRow): string {
  switch (row.account_cta_status) {
    case "from_account": {
      const parts = [row.source_organization_name, row.source_cta_name].filter(
        (x): x is string => typeof x === "string" && x !== "",
      );
      return `Set up from ${parts.length > 0 ? parts.join(" · ") : "the account's CTA"}.`;
    }
    // Internal audit P2-2: "prepared_without_account_cta" also covers types that never copy (classes, tables), a
    // source CTA deleted later, and codes typed by hand; "not_prepared" covers events set up before automatic
    // preparation. None of these means the QR is wrong, so the wording stays neutral.
    case "prepared_without_account_cta":
    case "deferred":
    case "not_prepared":
    default:
      return "Set up by the event team (concierge).";
  }
}
